import {
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import * as React from "react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { DataTablePagination } from "./data-table-pagination";
import { DataTableToolbar } from "./data-table-toolbar";
import { useTranslation } from "react-i18next";

export function DataTable({
  columns,
  data,
  mainFilterColumn,
  filterOptions,
  textPlaceholder,
  server,
}) {
  const { t } = useTranslation();
  const [rowSelection, setRowSelection] = React.useState({});
  const [columnFilters, setColumnFilters] = React.useState([]);
  const [sorting, setSorting] = React.useState([{ id: "id", desc: true }]); // Orden descendente inicial
  const [pagination, setPagination] = React.useState({ pageIndex: 0, pageSize: 10 });

  const isServerMode = Boolean(server);

  // Asegurarse de que data y columns sean arrays válidos
  const safeData = React.useMemo(() => Array.isArray(data) ? data : [], [data]);
  const safeColumns = React.useMemo(() => Array.isArray(columns) ? columns : [], [columns]);

  const handleSortingChange = (updater) => {
    const next =
      typeof updater === "function" ? updater(sorting) : updater;
    setSorting(next);
    if (isServerMode) {
      server.onSortingChange?.(next);
    }
  };

  const handleColumnFiltersChange = (updater) => {
    const next =
      typeof updater === "function" ? updater(columnFilters) : updater;
    setColumnFilters(next);
    if (isServerMode) {
      server.onFiltersChange?.(next);
    }
  };

  const handlePaginationChange = (updater) => {
    if (!isServerMode) {
      setPagination((current) =>
        typeof updater === "function" ? updater(current) : updater,
      );
      return;
    }
    const current = server.pagination;
    const next =
      typeof updater === "function" ? updater(current) : updater;
    server.onPaginationChange?.(next);
  };

  const table = useReactTable({
    data: safeData,
    columns: safeColumns,
    state: {
      sorting,
      rowSelection,
      columnFilters,
      pagination: isServerMode ? server.pagination : pagination,
    },
    pageCount: isServerMode ? server.pageCount : undefined,
    manualPagination: isServerMode,
    manualSorting: isServerMode,
    manualFiltering: isServerMode,
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: handleSortingChange,
    onColumnFiltersChange: handleColumnFiltersChange,
    onPaginationChange: handlePaginationChange,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  });

  return (
    <div className="space-y-4" data-testid="data-table">
      <DataTableToolbar
        table={table}
        mainFilterColumn={mainFilterColumn}
        filterOptions={filterOptions}
        textPlaceholder={textPlaceholder}
      />
      <div className="rounded-lg border overflow-x-auto">
        <Table className="min-w-full">
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id} colSpan={header.colSpan}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={safeColumns.length}
                  className="h-24 text-center"
                >
                  {t("table.noResults")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <DataTablePagination table={table} />
    </div>
  );
}
